import { supabase } from '../services/supabaseClient';
// Helper for browser → Supabase edge function calls.
//
// Background: Supabase enforces JWT verification on the edge-function gateway
// by default. The new `sb_publishable_*` API keys are NOT JWTs, so passing
// them in the Authorization header yields:
//   {"code":"UNAUTHORIZED_INVALID_JWT_FORMAT","message":"Invalid JWT"}
//
// The fix is to pass the current user's session access_token as the Bearer
// token (which IS a real JWT) and keep the publishable key in `apikey`.
// Every browser → edge function fetch in this codebase should go through
// edgeFetch() instead of building headers ad-hoc.

export async function edgeFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  // Fall back to the publishable key when no user is signed in — works for
  // any function deployed with --no-verify-jwt, returns 401 otherwise.
  const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  const url = path.startsWith('http')
    ? path
    : `${import.meta.env.VITE_SUPABASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  const headers = {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(init.headers ?? {}),
  };
  return fetch(url, { ...init, headers });
}

// Convenience: POST JSON to /functions/v1/<name>.
// S53 (2026-06-06) — accept an optional AbortSignal so callers can
// time the request out (e.g. signup-with-invite uses a 20s timeout
// to surface a friendly error instead of spinning forever when
// Supabase rate-limits the request).
export async function callEdgeFunction(
  name: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  return edgeFetch(`/functions/v1/${name}`, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  });
}
